import React, { useContext } from 'react';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import {
  TaskExportConfiguration,
  TaskExportField,
} from '../../../../../../../types/task_export_configuration/taskExportConfiguration';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../../../types/translation/Translator';
import { Checkbox } from '../../../../../../components/Checkbox';
import NotFound from '../../../../../../components/NotFound/NotFound';
import { CustomFieldContext } from '../../../../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../../../../types/custom_field';
import { TaskExportConfigurationInput } from '../../../../../../../types/task_export_configuration';
import ExportConfigurationForm, {
  DraggableExportFieldIdentifier,
} from '../../../../../../components/Export/ExportConfigurationForm';

interface TaskExportConfigurationMapping {
  value: string;
  index: number;
  field: TaskExportField;
}

export interface DraggableTaskExportFieldIdentifier
  extends DraggableExportFieldIdentifier<TaskExportField>,
    TaskExportConfigurationMapping {}

interface TaskExportConfigurationFormProps {
  input: TaskExportConfigurationInput;
  setInput: React.Dispatch<React.SetStateAction<TaskExportConfigurationInput>>;
  footer: (configuration: TaskExportConfigurationInput, items: DraggableTaskExportFieldIdentifier[]) => JSX.Element;
  loading?: boolean;
}

export default function TaskExportConfigurationForm({
  input,
  setInput,
  footer,
  loading = false,
}: TaskExportConfigurationFormProps) {
  if (!input) return <NotFound />;

  const { customFields } = useContext(CustomFieldContext);

  const draggableItems: DraggableTaskExportFieldIdentifier[] =
    DraggableItemData.forExportConfiguration<TaskExportField>([
      ...Object.values(TaskExportField).map(field => ({ field, label: translate({ key: field }) })),
      ...[...customFields.values()]
        .filter(
          item =>
            item.entityType === CustomFieldEntityType.productMasterData ||
            item.entityType === CustomFieldEntityType.product ||
            item.entityType === CustomFieldEntityType.lot ||
            item.entityType === CustomFieldEntityType.bin ||
            item.entityType === CustomFieldEntityType.container,
        )
        .map(item => ({ field: item.id, label: item.name })),
    ]);

  return (
    <ExportConfigurationForm
      placeholder={translate({ key: TranslationKey.addAdditionalTaskExportField })}
      loading={loading}
      input={input}
      setInput={setInput}
      footer={footer}
      draggableItems={draggableItems}
      toDraggableItems={TaskExportConfiguration.toDraggableItems}
      additionalOptions={configuration => (
        <>
          <div>
            <Checkbox
              value={configuration.discrepancyOnly}
              label={translate({ key: TranslationKey.discrepancyOnly })}
              onChange={checked => setInput(configuration.withDiscrepancy(checked))}
            />
          </div>
          <div>
            <Checkbox
              value={configuration.countedOnly}
              label={translate({ key: TranslationKey.countedOnly })}
              onChange={checked => setInput(configuration.withCountedOnly(checked))}
            />
          </div>
        </>
      )}
    />
  );
}
